import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, ArrowRightLeft } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

import { fmtMoney, fmtDate } from "@/lib/format";
export default function InterCompanyLoans() {
  const { can } = usePermissions();
  const canCreateLoan = can("INTERCOMPANY_LOANS", "create");
  const { companies } = useCompany() || {};
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gl/intercompany-loans");
      setLoans(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || id;

  return (
    <div className="space-y-6" data-testid="intercompany-loans-page">
      <PageHeader
        title="Inter-company Loans"
        description="Posts a mirrored pair of journal entries — cash out of the lender's books, in to the borrower's."
        action={canCreateLoan && <CreateLoanDialog companies={companies} onCreated={load} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && loans.length === 0 ? (
          <EmptyState title="No inter-company loans yet" testId="intercompany-loans-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Lender</TableHead><TableHead></TableHead><TableHead>Borrower</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {loans.map((l) => (
                <TableRow key={l.id} data-testid={`loan-row-${l.id}`}>
                  <TableCell className="text-muted-foreground">{fmtDate(l.loan_date)}</TableCell>
                  <TableCell className="text-foreground">{companyName(l.lender_company_id)}</TableCell>
                  <TableCell><ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground" /></TableCell>
                  <TableCell className="text-foreground">{companyName(l.borrower_company_id)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{fmtMoney(l.amount)}</TableCell>
                  <TableCell><Badge>{l.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateLoanDialog({ companies, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lender_company_id: "", borrower_company_id: "", amount: "", loan_date: "", interest_rate: "", narration: "" });
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({ lender_company_id: "", borrower_company_id: "", amount: "", loan_date: "", interest_rate: "", narration: "" }); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.lender_company_id === form.borrower_company_id) {
      toast.error("Lender and borrower must be different companies");
      return;
    }
    setSaving(true);
    try {
      await api.post("/gl/intercompany-loans", { ...form, interest_rate: form.interest_rate || null });
      toast.success("Inter-company loan posted");
      setForm({ lender_company_id: "", borrower_company_id: "", amount: "", loan_date: "", interest_rate: "", narration: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-loan-button"><Plus className="h-4 w-4" /> New Loan</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Inter-company Loan</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-loan-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Lender company</Label>
              <Select required value={form.lender_company_id} onValueChange={(v) => setForm({ ...form, lender_company_id: v })}>
                <SelectTrigger data-testid="loan-lender-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Borrower company</Label>
              <Select required value={form.borrower_company_id} onValueChange={(v) => setForm({ ...form, borrower_company_id: v })}>
                <SelectTrigger data-testid="loan-borrower-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount</Label><Input required type="number" step="0.01" data-testid="loan-amount-input" value={form.amount} onChange={set("amount")} className="mt-1.5" /></div>
            <div><Label>Date</Label><Input required type="date" data-testid="loan-date-input" value={form.loan_date} onChange={set("loan_date")} className="mt-1.5" /></div>
          </div>
          <div><Label>Narration</Label><Input data-testid="loan-narration-input" value={form.narration} onChange={set("narration")} className="mt-1.5" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-loan-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Post loan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
